import BusinessApiService from "../apiServices/BusinessApiService";
import ProductApiService from "../apiServices/ProductApiService";
import BusinessSectionApiService from "../apiServices/BusinessSectionApiService";
import { extractBusinessIdFromParam } from "../helpers/BusinessSlugHelper";
import { BUSINESS_PAGE_PATH_RE } from "../../constants/RoutePatterns";

/**
 * Business-page data prefetch.
 *
 * On a cold load of /business/:id, main.tsx calls startBusinessPagePrefetch()
 * before React renders, so the business, products and section-tree requests
 * are already in flight by the time BusinessListings mounts.
 *
 * The thunks (fetchBusinessById / fetchBusinessListings / fetchSectionTree)
 * pick up the in-flight promise via take*() instead of firing a duplicate
 * request, so Redux state, loading flags and error handling are unchanged.
 * Each promise is single-use, only reused for the same businessId, and
 * expires after PREFETCH_TTL_MS -- if the visitor leaves before the page
 * mounts, a later visit fetches fresh data instead of the page-load response.
 */

const PREFETCH_TTL_MS = 10_000;

interface PrefetchSlot<T> {
  start: (businessId: string) => void;
  take: (businessId: string) => Promise<T> | null;
}

function createPrefetchSlot<T>(request: (businessId: string) => Promise<T>): PrefetchSlot<T> {
  let entry: { businessId: string; promise: Promise<T>; startedAt: number } | null = null;
  return {
    start(businessId) {
      const promise = request(businessId);
      // Errors surface through the thunk that takes the promise; this no-op
      // handler only stops an "unhandled rejection" if it is never taken.
      promise.catch(() => undefined);
      entry = { businessId, promise, startedAt: Date.now() };
    },
    take(businessId) {
      const current = entry;
      entry = null;
      if (!current || current.businessId !== businessId) return null;
      if (Date.now() - current.startedAt > PREFETCH_TTL_MS) return null;
      return current.promise;
    },
  };
}

const businessSlot = createPrefetchSlot(BusinessApiService.getBusinessById);
const productsSlot = createPrefetchSlot(ProductApiService.getProductsByBusinessId);
// Products are grouped by section, so the tree is needed for first paint too.
const sectionsSlot = createPrefetchSlot(BusinessSectionApiService.getSectionTree);

export function startBusinessPagePrefetch(pathname: string): void {
  const match = pathname.match(BUSINESS_PAGE_PATH_RE);
  if (!match) return;
  const businessId = extractBusinessIdFromParam(decodeURIComponent(match[1]));
  if (!businessId) return;
  businessSlot.start(businessId);
  productsSlot.start(businessId);
  sectionsSlot.start(businessId);
}

export const takePrefetchedBusiness = businessSlot.take;
export const takePrefetchedProducts = productsSlot.take;
export const takePrefetchedSections = sectionsSlot.take;
